// Validation utility for form fields
import isEmail from 'validator/lib/isEmail';
import { isLenderPlan, isRealEstatePlan, requiresSubscriptionLength } from './services/planService';

export const validatePaymentForm = (formData) => {
  const errors = {};

  if (!formData.firstName.trim()) {
    errors.firstName = 'First name is required';
  }

  if (!formData.lastName.trim()) {
    errors.lastName = 'Last name is required';
  }

  if (!formData.email.trim()) {
    errors.email = 'Email is required';
  } else {
    const email = formData.email.trim();
    
    if (!isEmail(email, {
      allow_display_name: false,
      require_display_name: false,
      allow_utf8_local_part: false,
      require_tld: true,
      allow_ip_domain: false,
      domain_specific_validation: true,
      blacklisted_chars: '',
      host_blacklist: []
    })) {
      errors.email = 'Please enter a valid email address';
    } else {
      // Additional business rules
      const domain = email.split('@')[1]?.toLowerCase();
      
      // Reject some common fake/test domains
      const invalidDomains = ['test.com', 'example.com', 'localhost', 'test.test', 'fake.com'];
      if (invalidDomains.includes(domain)) {
        errors.email = 'Please enter a valid business email address';
      } else {
        // Check for disposable email domains (common ones)
        const disposableDomains = [
          '10minutemail.com', 'guerrillamail.com', 'mailinator.com', 'tempmail.org',
          'yopmail.com', 'throwaway.email', 'getnada.com', 'sharklasers.com'
        ];
        if (disposableDomains.includes(domain)) {
          errors.email = 'Please use a permanent business email address';
        }
      }
    }
  }

  // NMLS ID is only required for lender plans
  if (isLenderPlan(formData.plan) && !formData.nmlsId.trim()) {
    errors.nmlsId = 'NMLS ID is required';
  }

  // State License is required for real estate agent plans only
  if (isRealEstatePlan(formData.plan) && !formData.stateLicense.trim()) {
    errors.stateLicense = 'State License is required';
  }

  // Billing cadence is required for certain lender plans
  if (requiresSubscriptionLength(formData.plan) && formData.subscriptionLength === 'none') {
    errors.subscriptionLength = 'Please select a billing period';
  }

  if (!formData.termsAccepted) {
    errors.termsAccepted = 'You must accept the terms and conditions';
  }

  // Office selection is required when companyId is present
  if (formData.companyId && !formData.selectedOffice) {
    errors.selectedOffice = 'Please select an office';
  }

  if (formData.paymentMethod === 'card') {
    if (!formData.cardFirstName.trim()) {
      errors.cardFirstName = 'First name on card is required';
    }

    if (!formData.cardLastName.trim()) {
      errors.cardLastName = 'Last name on card is required';
    }

    if (!formData.billingAddress.trim()) {
      errors.billingAddress = 'Billing address is required';
    }

    if (!formData.billingZip.trim()) {
      errors.billingZip = 'Billing zip code is required';
    } else if (!/^\d{5}(-\d{4})?$/.test(formData.billingZip)) {
      errors.billingZip = 'Please enter a valid zip code';
    }
  } else if (formData.paymentMethod === 'bank') {
    if (!formData.accountType) {
      errors.accountType = 'Account type is required';
    }
    
    if (!formData.nameOnAccount.trim()) {
      errors.nameOnAccount = 'Name on account is required';
    }
    
    if (!formData.accountNumber.trim()) {
      errors.accountNumber = 'Bank account number is required';
    }
    
    if (!formData.accountNumberConfirmation.trim()) {
      errors.accountNumberConfirmation = 'Please confirm your account number';
    } else if (formData.accountNumber !== formData.accountNumberConfirmation) {
      errors.accountNumberConfirmation = 'Account numbers do not match';
    }

    if (!formData.routingNumber.trim()) {
      errors.routingNumber = 'Routing number is required';
    } else if (!/^\d{9}$/.test(formData.routingNumber)) {
      errors.routingNumber = 'Please enter a valid 9-digit routing number';
    }

    // Bank accounts also need billing information
    if (!formData.billingAddress.trim()) {
      errors.billingAddress = 'Billing address is required';
    }

    if (!formData.billingCity.trim()) {
      errors.billingCity = 'Billing city is required';
    }

    if (!formData.billingState) {
      errors.billingState = 'Billing state is required';
    }

    if (!formData.billingZip.trim()) {
      errors.billingZip = 'Billing zip code is required';
    } else if (!/^\d{5}(-\d{4})?$/.test(formData.billingZip)) {
      errors.billingZip = 'Please enter a valid zip code';
    }
  }

  return errors;
}; 
